import { useLocalStorage } from '@mantine/hooks';

import { DEFAULT_RATE_SORT_OPTION, RATE_SORT_OPTION } from './rate-sorting';

import type { RateSortOption } from './rate-sorting';

export const RATE_SORT_OPTION_STORAGE_KEY = 'freightRatesSortOption';

const VALID_SORT_OPTIONS: Set<string> = new Set(Object.values(RATE_SORT_OPTION));

const isRateSortOption = (value: unknown): value is RateSortOption => typeof value === 'string' && VALID_SORT_OPTIONS.has(value);

// localStorage is a plain string slot shared across tabs/sessions — any stored value must be
// validated before use. A stale value from a removed sort option (or manual tampering) must
// fall back to the default rather than reach sortCarrierRows' SORT_CONFIG lookup, which throws
// on an unrecognized key.
const deserializeSortOption = (value: string | undefined): RateSortOption => {
  if (value === undefined) return DEFAULT_RATE_SORT_OPTION;

  try {
    const parsed = JSON.parse(value);
    return isRateSortOption(parsed) ? parsed : DEFAULT_RATE_SORT_OPTION;
  } catch {
    return DEFAULT_RATE_SORT_OPTION;
  }
};

/**
 * Persists the freight rate sort choice across drawer close/reopen, wizard mode changes, and
 * page reloads — none of which survive plain component-local state, since both RatesPanel and
 * WizardRatesPanel live inside a Mantine Drawer without `keepMounted`. Shared across both panels
 * so a choice made in the browser carries over to the wizard for the same user.
 */
export const useRateSortOption = (): [RateSortOption, (next: RateSortOption) => void] => {
  const [sortOption, setSortOption] = useLocalStorage<RateSortOption>({
    key: RATE_SORT_OPTION_STORAGE_KEY,
    defaultValue: DEFAULT_RATE_SORT_OPTION,
    deserialize: deserializeSortOption,
  });

  return [sortOption, setSortOption];
};
